import axios from "axios";
import type { AxiosRequestConfig, AxiosError, InternalAxiosRequestConfig } from "axios";
import type { ErrorVO } from "../business/common/error-vo.js";
import { parseJsonOffThread } from "./json-parse-client.js";

// 模块级 axios 实例，供 Orval mutator 和手动使用
// timeout：默认 2 分钟，防止半开连接导致请求无限挂起（大文件上传/下载处显式传 timeout: 0）
export const apiClient = axios.create({
  headers: { "Content-Type": "application/json" },
  timeout: 120_000,
  // 数组 query 参数序列化为重复键（status=a&status=b，对齐 OpenAPI form+explode 标准语义）。
  // axios 默认的 status[]=a 形式 Fastify querystring 解析为字面量 "status[]"，
  // Ts.ED @QueryParams 绑定不到 → 数组过滤参数被后端静默忽略
  paramsSerializer: { indexes: null },
});

let getUiLocale = (): string => "zh-CN";

export function setupUiLocale(getLocale: () => string): void {
  getUiLocale = getLocale;
}

// --- Worker parse：大响应 JSON 在后台线程解析，主线程不再被同步 JSON.parse 冻结 ---
// 白名单 = 已知的大响应只读接口；超过阈值（256KB）的响应文本交给 json-parse worker，
// 小响应主线程直接 parse（Worker 往返开销反而更慢）。
const WORKER_PARSE_URL = /\/(markdown|preview-mineru|chunks|effective-blocks)$/;
const WORKER_PARSE_MIN_BYTES = 256 * 1024;

declare module "axios" {
  export interface InternalAxiosRequestConfig {
    /** 响应文本交给 worker 解析（白名单请求由请求拦截器自动标记） */
    __workerParse?: boolean;
  }
}

apiClient.interceptors.request.use((config) => {
  config.headers.set("Accept-Language", getUiLocale());
  if (config.url && WORKER_PARSE_URL.test(config.url.split("?")[0])) {
    config.__workerParse = true;
    // 跳过 axios 默认 transformResponse 的主线程 JSON.parse，保留原始字符串给 worker
    config.transformResponse = [(data: unknown) => data];
  }
  return config;
});

apiClient.interceptors.response.use(async (response) => {
  const cfg = response.config as InternalAxiosRequestConfig;
  if (cfg.__workerParse && typeof response.data === "string") {
    if (response.data.length >= WORKER_PARSE_MIN_BYTES) {
      response.data = await parseJsonOffThread(response.data);
    } else {
      response.data = JSON.parse(response.data);
    }
  }
  return response;
});

// 后端 GlobalErrorFilter 错误体为 ErrorVO（key/msg/sln），msg 已是 i18n 中文文案。
// 写入 error.message，让组件的 err.message 显示真实业务原因，
// 而非 axios 默认的 "Request failed with status code 409"。
// 网络错误/超时无 response，message 保持 axios 原文（Network Error / timeout）。
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ErrorVO>) => {
    const msg = error.response?.data?.msg;
    if (typeof msg === "string" && msg) {
      error.message = msg;
    }
    return Promise.reject(error);
  },
);

// 无 body 的 DELETE 不发 Content-Type：Fastify 对「json content-type + 空 body」直接拒绝
apiClient.interceptors.request.use((config) => {
  if (config.method?.toLowerCase() === "delete" && config.data === undefined) {
    config.headers.delete("Content-Type");
  }
  return config;
});

// --- 认证拦截器配置 ---

export interface AuthInterceptorDeps {
  getToken: () => string | null;
  setToken: (token: string) => void;
  clearAuth: () => void;
  onAuthExpired: () => void;
  onAuthUnavailable?: () => void;
  /** 当前租户 id（持久化值），自动刷新时带上以保持租户上下文 */
  getActiveTenantId?: () => string | null;
}

let isRefreshing = false;
let pendingRequests: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

function processPending(error: unknown, token?: string) {
  pendingRequests.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else if (token) resolve(token);
  });
  pendingRequests = [];
}

export function isAuthExpiredError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 401;
}

export function isTemporaryAuthFailure(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;

  const status = error.response?.status;
  return !error.response || status === undefined || status >= 500;
}

/**
 * 安装认证相关拦截器。在 app 入口调用一次。
 * - 请求拦截器：从 deps.getToken() 读取 token 附加到 Authorization header
 * - 响应拦截器：401 时自动刷新 token，仅 refresh 明确 401 时退出登录
 */
export function setupAuthInterceptors(deps: AuthInterceptorDeps) {
  apiClient.interceptors.request.use((config) => {
    const token = deps.getToken();
    if (token) {
      config.headers.set("Authorization", `Bearer ${token}`);
    }
    return config;
  });

  apiClient.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config;
      if (
        !originalRequest ||
        error.response?.status !== 401 ||
        (originalRequest as AxiosRequestConfig & { _retry?: boolean })._retry
      ) {
        return Promise.reject(error);
      }

      // Refresh 和一次性票据兑换不自动重试，避免循环或恢复旧账号后重用票据。
      if (
        originalRequest.url?.includes("/intl/auth/refresh") ||
        originalRequest.url?.includes("/intl/auth/session-handoff/exchange")
      ) {
        return Promise.reject(error);
      }

      const currentToken = deps.getToken();
      if (!currentToken) return Promise.reject(error);

      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          pendingRequests.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.set("Authorization", `Bearer ${token}`);
          return apiClient(originalRequest);
        });
      }

      (originalRequest as AxiosRequestConfig & { _retry?: boolean })._retry =
        true;
      isRefreshing = true;

      try {
        const activeTenantId = deps.getActiveTenantId?.();
        // 自动刷新带上租户 id，保持切换后的租户上下文；
        // 若该租户已失效（如用户被移出）则回退默认租户刷新
        const { data } = await apiClient
          .post<{
            token?: { accessToken?: string };
          }>(
            "/intl/auth/refresh",
            activeTenantId ? { tenantId: activeTenantId } : undefined,
          )
          .catch((err: unknown) =>
            activeTenantId
              ? apiClient.post<{ token?: { accessToken?: string } }>(
                  "/intl/auth/refresh",
                )
              : Promise.reject(err),
          );
        const newToken = data.token?.accessToken;
        if (!newToken) throw new Error("No token in refresh response");

        deps.setToken(newToken);
        processPending(null, newToken);

        originalRequest.headers.set("Authorization", `Bearer ${newToken}`);
        return apiClient(originalRequest);
      } catch (refreshError) {
        processPending(refreshError);
        if (isAuthExpiredError(refreshError)) {
          deps.clearAuth();
          deps.onAuthExpired();
        } else if (isTemporaryAuthFailure(refreshError)) {
          deps.onAuthUnavailable?.();
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    },
  );
}

// Orval mutator 函数：封装 apiClient，自动 unwrap response.data
// extraConfig 可透传进度、超时和取消信号，供生成的 mutation hooks 使用。
export const apiRequest = async <T>(
  config: AxiosRequestConfig,
  extraConfig?: Pick<AxiosRequestConfig, "onUploadProgress" | "onDownloadProgress" | "timeout" | "signal">,
): Promise<T> => {
  const response = await apiClient({ ...config, ...extraConfig });
  return response.data;
};

// 二进制响应专用：返回 Blob，用于文件预览/下载（大文件不限制时）
export const apiBinaryRequest = async <_T = Blob>(config: AxiosRequestConfig): Promise<Blob> => {
  const response = await apiClient({
    ...config,
    responseType: "blob",
    timeout: 0,
  });
  return response.data;
};
